// Struktogramm-Interpreter im Browser -- muss sich exakt wie structolab/expr.py und
// structolab/interpreter.py verhalten (gemeinsame Testfälle in tests/fixtures/*.json).
// Zusätzlich: Runner zum schrittweisen Ausführen im Editor.

export const MAX_INT = Number.MAX_SAFE_INTEGER; // 2^53 - 1, wie im Python-Original

export const SIMPLE_TYPES = ["anweisung", "eingabe", "ausgabe"] as const;
export const LOOP_TYPES = ["solange", "wiederhole", "zaehlschleife"] as const;
export const BLOCK_TYPES = [...SIMPLE_TYPES, "verzweigung", ...LOOP_TYPES] as const;
export const MAX_DEPTH = 12;
export const MAX_BLOCKS = 200;

/** Ein Block im Struktogramm; Verzweigungen haben ja/nein, Schleifen body. */
export interface SBlock {
  id: string; type: string; text?: string;
  body?: SBlock[]; ja?: SBlock[]; nein?: SBlock[];
}
export interface Struktogramm { body: SBlock[] }
export type Field = "body" | "ja" | "nein";

export type Wert = number | string;
export interface Testfall { eingabe: number[]; ausgabe: Wert[] }
export interface Task {
  id: string; titel: string; text: string;
  eingaben: string[]; ausgaben: string[]; testfaelle: Testfall[];
  bloecke: Record<string, number>; par: number | null; max_schritte: number;
  hinweise: string[]; block: string | null;
  optional?: boolean; // Zusatzaufgabe für Schnelle
}

export interface Problem { code: string; message: string; ref: string | null }

// ------------------------------------------------------------------------- Ausdrücke

export class ExprError extends Error {}
export class RunError extends Error {}

export type Node =
  | { k: "num"; value: number }
  | { k: "var"; name: string }
  | { k: "text"; value: string }
  | { k: "neg"; arg: Node }
  | { k: "not"; arg: Node }
  | { k: "bin"; op: string; left: Node; right: Node };

const ARITH = new Set(["+", "-", "*", "/", "mod"]);
const COMPARE = new Set(["==", "!=", "<", "<=", ">", ">="]);

const NAME = "[A-Za-zÄÖÜäöüß_][A-Za-z0-9ÄÖÜäöüß_]*";
const NAME_RE = new RegExp(`^${NAME}$`);
const WORDS: Record<string, string> = {
  und: "and", and: "and", oder: "or", or: "or", nicht: "not", not: "not", mod: "mod", div: "/",
};
const SYMBOLS: Record<string, string> = {
  "<=": "<=", ">=": ">=", "==": "==", "!=": "!=", "<>": "!=", "&&": "and", "||": "or",
  "≤": "<=", "≥": ">=", "≠": "!=", "<": "<", ">": ">", "=": "==",
  "+": "+", "-": "-", "−": "-", "*": "*", "·": "*", "×": "*", "/": "/", ":": "/",
  "%": "mod", "!": "not", "(": "(", ")": ")", ",": ",",
};
const TOKEN_RE = new RegExp(
  String.raw`\s*(?:"([^"]*)"|„([^“”"]*)[“”"]|“([^”]*)”|'([^']*)'` +
  String.raw`|([0-9]+(?:[.,][0-9]+)?)` +
  `|(${NAME})` +
  String.raw`|(<=|>=|==|!=|<>|&&|\|\||.))`, "y");

type Tok = [kind: "text" | "num" | "name" | "op", val: string | number];

function tokenize(src: string): Tok[] {
  const tokens: Tok[] = [];
  const text = src.trimEnd();
  let pos = 0;
  while (pos < text.length) {
    TOKEN_RE.lastIndex = pos;
    const m = TOKEN_RE.exec(text)!;
    pos = TOKEN_RE.lastIndex;
    const [, s1, s2, s3, s4, number, word, sym] = m;
    const str = [s1, s2, s3, s4].find(s => s !== undefined);
    if (str !== undefined) {
      tokens.push(["text", str]);
    } else if (number !== undefined) {
      if (!/^[0-9]+$/.test(number)) throw new ExprError(`Nur ganze Zahlen erlaubt, nicht ${number}`);
      if (Number(number) > MAX_INT) throw new ExprError(`Zahl ${number} ist zu groß`);
      tokens.push(["num", Number(number)]);
    } else if (word !== undefined) {
      const low = word.toLowerCase();
      tokens.push(low in WORDS ? ["op", WORDS[low]] : ["name", word]);
    } else if (sym !== undefined && sym.trim()) {
      if (!(sym in SYMBOLS)) throw new ExprError(`Unbekanntes Zeichen '${sym}'`);
      tokens.push(["op", SYMBOLS[sym]]);
    }
  }
  return tokens;
}

function show(tok: Tok | undefined): string {
  if (!tok) return "Ende";
  return tok[0] === "text" ? `"${tok[1]}"` : `'${tok[1]}'`;
}

class Parser {
  tokens: Tok[];
  i = 0;
  constructor(text: string | Tok[]) { this.tokens = typeof text === "string" ? tokenize(text) : text; }

  peek(): Tok | undefined { return this.tokens[this.i]; }
  peekOp(): string | null {
    const t = this.peek();
    return t && t[0] === "op" ? (t[1] as string) : null;
  }
  take(): Tok {
    const t = this.peek();
    if (!t) throw new ExprError("Ausdruck endet unerwartet");
    this.i++;
    return t;
  }
  atEnd(): boolean { return this.i >= this.tokens.length; }
  expectEnd(): void { if (!this.atEnd()) throw new ExprError(`Unerwartetes ${show(this.peek())}`); }

  expr(): Node {
    let node = this.conj();
    while (this.peekOp() === "or") { this.take(); node = { k: "bin", op: "or", left: node, right: this.conj() }; }
    return node;
  }
  conj(): Node {
    let node = this.neg();
    while (this.peekOp() === "and") { this.take(); node = { k: "bin", op: "and", left: node, right: this.neg() }; }
    return node;
  }
  neg(): Node {
    if (this.peekOp() === "not") { this.take(); return { k: "not", arg: this.neg() }; }
    return this.compare();
  }
  compare(): Node {
    let node = this.sum();
    const op = this.peekOp();
    if (op && COMPARE.has(op)) {
      this.take();
      node = { k: "bin", op, left: node, right: this.sum() };
      const again = this.peekOp();
      if (again && COMPARE.has(again)) {
        throw new ExprError("Vergleichsketten wie a < b < c gehen nicht -- " +
                            "bitte mit 'und' verbinden: a < b und b < c");
      }
    }
    return node;
  }
  sum(): Node {
    let node = this.term();
    for (let op = this.peekOp(); op === "+" || op === "-"; op = this.peekOp()) {
      this.take();
      node = { k: "bin", op, left: node, right: this.term() };
    }
    return node;
  }
  term(): Node {
    let node = this.unary();
    for (let op = this.peekOp(); op === "*" || op === "/" || op === "mod"; op = this.peekOp()) {
      this.take();
      node = { k: "bin", op, left: node, right: this.unary() };
    }
    return node;
  }
  unary(): Node {
    if (this.peekOp() === "-") { this.take(); return { k: "neg", arg: this.unary() }; }
    if (this.peekOp() === "+") { this.take(); return this.unary(); }
    const [kind, val] = this.take();
    if (kind === "num") return { k: "num", value: val as number };
    if (kind === "name") return { k: "var", name: val as string };
    if (kind === "op" && val === "(") {
      const node = this.expr();
      const close = this.atEnd() ? null : this.take();
      if (!close || close[0] !== "op" || close[1] !== ")") throw new ExprError("Fehlende schließende Klammer");
      return node;
    }
    if (kind === "text") throw new ExprError("Texte in Anführungszeichen sind nur in Ausgabe-Blöcken erlaubt");
    throw new ExprError(`Unerwartetes ${show([kind, val])}`);
  }
}

type Kind = "zahl" | "bedingung";

export function kind(node: Node): Kind {
  switch (node.k) {
    case "num": case "var": return "zahl";
    case "text": throw new ExprError("Text ist hier nicht erlaubt");
    case "neg":
      need(node.arg, "zahl", "Vor einem Minus muss eine Zahl stehen");
      return "zahl";
    case "not":
      need(node.arg, "bedingung", "'nicht' braucht eine Bedingung, z. B. nicht (a > b)");
      return "bedingung";
    case "bin":
      if (ARITH.has(node.op) || COMPARE.has(node.op)) {
        need(node.left, "zahl", `Links von '${node.op}' muss eine Zahl stehen, keine Bedingung`);
        need(node.right, "zahl", `Rechts von '${node.op}' muss eine Zahl stehen, keine Bedingung`);
        return ARITH.has(node.op) ? "zahl" : "bedingung";
      } else {
        const word = node.op === "and" ? "und" : "oder";
        const msg = `'${word}' verbindet Bedingungen, z. B. a > 0 ${word} b > 0`;
        need(node.left, "bedingung", msg);
        need(node.right, "bedingung", msg);
        return "bedingung";
      }
  }
}

function need(node: Node, want: Kind, message: string): void {
  if (kind(node) !== want) throw new ExprError(message);
}

// ------------------------------------------------------------------------- Block-Inhalte

const ASSIGN_RE = new RegExp(`^\\s*(${NAME})\\s*(?::=|←|<-|=(?!=))([\\s\\S]*)$`);
const INPUT_PREFIX = /^\s*(?:eingabe|lies)\b\s*:?\s*(?=\S)/i;
const OUTPUT_PREFIX = /^\s*(?:ausgabe|ausgeben|gib\s+aus)\b\s*:?\s*(?=\S)/i;
const COND_PREFIX: Record<string, RegExp> = {
  wenn: /^\s*(?:wenn|falls)\s+(?=\S)/i,
  solange: /^\s*solange\s+(?=\S)/i,
  bis: /^\s*bis\s+(?=\S)/i,
};
const COUNT_RE = new RegExp(`^\\s*(?:(?:für|fuer|for)\\s+)?(${NAME})\\s*(?::=|←|<-|=(?!=)|von(?=\\s))([\\s\\S]*)$`, "i");
export const COUNT_EXAMPLE = "Zählschleife braucht Zählvariable, Start und Ende, z. B. i = 1 bis n";

function checkName(name: string): string {
  if (!NAME_RE.test(name)) throw new ExprError(`'${name}' ist kein gültiger Variablenname`);
  if (name.toLowerCase() in WORDS) throw new ExprError(`'${name}' ist ein reserviertes Wort und kein Variablenname`);
  return name;
}

function full(text: string | Tok[], want: Kind): Node {
  const p = new Parser(text);
  if (p.atEnd()) throw new ExprError("Der Block ist leer");
  const node = p.expr();
  p.expectEnd();
  if (kind(node) !== want) {
    throw new ExprError(want === "bedingung" ? "Hier wird eine Bedingung erwartet, z. B. a > b"
                                             : "Hier wird eine Zahl erwartet, keine Bedingung");
  }
  return node;
}

export function parseAssignment(text: string): [string, Node] {
  const m = ASSIGN_RE.exec(text);
  if (!m) throw new ExprError("Anweisung braucht eine Zuweisung, z. B. x = x + 1");
  return [checkName(m[1]), full(m[2], "zahl")];
}

export function parseCondition(text: string, keyword: string | null = null): Node {
  const t = (keyword ? text.replace(COND_PREFIX[keyword], "") : text).trimEnd();
  return full(t.endsWith("?") ? t.slice(0, -1) : t, "bedingung");
}

export function parseInput(text: string): string[] {
  const body = text.replace(INPUT_PREFIX, "").trim();
  if (!body) throw new ExprError("Eingabe braucht mindestens einen Variablennamen, z. B. a, b");
  const raw = body.split(",").map(n => n.trim());
  if (!raw.every(Boolean)) throw new ExprError("Zwischen den Kommas fehlt ein Variablenname");
  const names = raw.map(checkName);
  if (new Set(names).size !== names.length) throw new ExprError("Dieselbe Variable wird mehrfach eingelesen");
  return names;
}

export type Count = [name: string, start: Node, end: Node, step: Node | null];

/** Kopf einer Zählschleife: Zählvariable, Start, Ende, Schrittweite oder null. */
export function parseCount(text: string): Count {
  const m = COUNT_RE.exec(text);
  if (!m) throw new ExprError(COUNT_EXAMPLE);
  const name = checkName(m[1]);
  const parts: Tok[][] = [[]];
  const words: string[] = [];
  for (const tok of tokenize(m[2])) {
    const word = tok[0] === "name" ? String(tok[1]).toLowerCase() : "";
    if (word === "bis" || word === "schritt") {
      words.push(word);
      parts.push([]);
    } else {
      parts[parts.length - 1].push(tok);
    }
  }
  const w = words.join(",");
  if ((w !== "bis" && w !== "bis,schritt") || !parts.every(p => p.length)) throw new ExprError(COUNT_EXAMPLE);
  const nodes = parts.map(p => full(p, "zahl"));
  return [name, nodes[0], nodes[1], nodes[2] ?? null];
}

export function parseOutput(text: string): Node[] {
  const p = new Parser(text.replace(OUTPUT_PREFIX, ""));
  if (p.atEnd()) throw new ExprError("Ausgabe braucht mindestens einen Wert, z. B. max");
  const items: Node[] = [];
  for (;;) {
    const tok = p.peek();
    const nxt = p.tokens[p.i + 1];
    if (tok && tok[0] === "text" && (!nxt || (nxt[0] === "op" && nxt[1] === ","))) {
      p.take();
      items.push({ k: "text", value: tok[1] as string });
    } else {
      const node = p.expr();
      if (kind(node) !== "zahl") throw new ExprError("Ausgegeben werden können Zahlen und Texte, keine Bedingungen");
      items.push(node);
    }
    if (p.atEnd()) return items;
    const sep = p.take();
    if (sep[0] !== "op" || sep[1] !== ",") {
      throw new ExprError(`Unerwartetes ${show(sep)} -- mehrere Werte mit Komma trennen`);
    }
  }
}

// ------------------------------------------------------------------------- Auswertung

export function checked(v: number): number {
  if (Math.abs(v) > MAX_INT) throw new RunError("Zahl zu groß (mehr als 9 Billiarden)");
  return v === 0 ? 0 : v; // kein -0
}

export function intDiv(a: number, b: number): number {
  if (b === 0) throw new RunError("Division durch 0");
  return checked(Number(BigInt(a) / BigInt(b))); // BigInt schneidet zur 0 hin ab, exakt
}

export function evaluate(node: Node, env: Map<string, number>): number | boolean {
  switch (node.k) {
    case "num": return node.value;
    case "var": {
      const v = env.get(node.name);
      if (v === undefined) throw new RunError(`Variable ${node.name} hat noch keinen Wert`);
      return v;
    }
    case "text": throw new Error("Text kann nicht ausgewertet werden");
    case "neg": return checked(-(evaluate(node.arg, env) as number));
    case "not": return !evaluate(node.arg, env);
    case "bin": {
      if (node.op === "and") return (evaluate(node.left, env) as boolean) && (evaluate(node.right, env) as boolean);
      if (node.op === "or") return (evaluate(node.left, env) as boolean) || (evaluate(node.right, env) as boolean);
      const a = evaluate(node.left, env) as number;
      const b = evaluate(node.right, env) as number;
      switch (node.op) {
        case "+": return checked(a + b);
        case "-": return checked(a - b);
        case "*": return checked(a * b);
        case "/": return intDiv(a, b);
        case "mod": intDiv(a, b); return checked(a % b); // % ist für ganze Zahlen exakt, Vorzeichen wie a
        case "==": return a === b;
        case "!=": return a !== b;
        case "<": return a < b;
        case "<=": return a <= b;
        case ">": return a > b;
        case ">=": return a >= b;
      }
    }
  }
  throw new Error(`Unbekannter Ausdruck ${JSON.stringify(node)}`);
}


// ------------------------------------------------------------------------- Struktogramm

export type Code = [string, Node] | string[] | Node[] | Node | Count;

export interface Block {
  id: string; type: string; code: Code | null;
  children: Partial<Record<Field, Block[]>>;
}

export interface Program {
  body: Block[];
  problems: Problem[];
  blockCount: number;
  hasLoop: boolean;
  nested: boolean;
}

const isSimple = (t: string) => (SIMPLE_TYPES as readonly string[]).includes(t);
const isLoop = (t: string) => (LOOP_TYPES as readonly string[]).includes(t);
const isBlockType = (t: string) => (BLOCK_TYPES as readonly string[]).includes(t);

export function childrenOf(type: string): Field[] {
  if (type === "verzweigung") return ["ja", "nein"];
  if (isLoop(type)) return ["body"];
  return [];
}

/** Übersetzt einen Block-Inhalt; wirft ExprError (für die Live-Prüfung beim Tippen). */
export function compileBlock(type: string, text: string): Code | null {
  switch (type) {
    case "anweisung": return parseAssignment(text);
    case "eingabe": return parseInput(text);
    case "ausgabe": return parseOutput(text);
    case "verzweigung": return parseCondition(text, "wenn");
    case "solange": return parseCondition(text, "solange");
    case "wiederhole": return parseCondition(text, "bis");
    case "zaehlschleife": return parseCount(text);
  }
  return null;
}

export function compileProgram(data: Struktogramm): Program {
  const prog: Program = { body: [], problems: [], blockCount: 0, hasLoop: false, nested: false };
  const P = (code: string, message: string, ref: string | null = null) => prog.problems.push({ code, message, ref });
  const seen = new Set<string>();

  const seq = (raw: unknown, depth: number, inControl: boolean): Block[] => {
    if (raw === undefined || raw === null) return [];
    if (!Array.isArray(raw)) throw new TypeError("Sequenz muss eine Liste sein");
    const out: Block[] = [];
    for (const b of raw as SBlock[]) {
      const id = String(b.id ?? ""), type = String(b.type ?? "").toLowerCase();
      prog.blockCount++;
      if (!id) { P("bad_id", "Block ohne ID", id); continue; }
      if (seen.has(id)) { P("duplicate_id", `Block-ID '${id}' doppelt`, id); continue; }
      seen.add(id);
      if (!isBlockType(type)) { P("unknown_type", `Unbekannter Blocktyp '${type}'`, id); continue; }
      const block: Block = { id, type, code: null, children: {} };
      try {
        block.code = compileBlock(type, String(b.text || ""));
      } catch (err) {
        if (!(err instanceof ExprError)) throw err;
        P("syntax", err.message, id);
      }
      const control = !isSimple(type);
      if (control && inControl) prog.nested = true;
      if (isLoop(type)) prog.hasLoop = true;
      if (control && depth >= MAX_DEPTH) {
        P("too_deep", `Höchstens ${MAX_DEPTH} Ebenen ineinander`, id);
        out.push(block);
        continue;
      }
      for (const key of childrenOf(type)) block.children[key] = seq(b[key], depth + 1, inControl || control);
      if (isLoop(type) && !block.children.body!.length) {
        P("empty_body", "Die Schleife ist leer -- ziehen Sie Blöcke hinein", id);
      }
      if (type === "verzweigung" && !block.children.ja!.length && !block.children.nein!.length) {
        P("empty_branch", "Beide Zweige der Verzweigung sind leer", id);
      }
      out.push(block);
    }
    return out;
  };

  prog.body = seq(data.body, 0, false);
  if (prog.blockCount > MAX_BLOCKS) prog.problems.unshift({ code: "too_big", message: `Höchstens ${MAX_BLOCKS} Blöcke`, ref: null });
  if (!prog.body.length && !prog.problems.length) P("empty", "Das Struktogramm ist noch leer");
  return prog;
}

// ------------------------------------------------------------------------- Ausführung

export type RunStatus = "läuft" | "beendet" | "endlosschleife" | "laufzeitfehler";

class Endless extends Error {}

/**
 * Schrittweise Ausführung eines fehlerfreien Programms (prog.problems leer). Ein Schritt ist
 * ein „Takt“ eines Blocks wie in interpreter.py; `cur` ist der Block, der als Nächstes dran
 * ist (vor dem ersten step(): der erste Block; am Ende: null).
 */
export class Runner {
  cur: string | null = null;
  env = new Map<string, number>();
  werte: Wert[] = [];
  schritte = 0;
  status: RunStatus = "läuft";
  fehler: string | null = null;
  ref: string | null = null;
  /** Zuletzt gewählter Zweig je Verzweigung und Durchläufe je Schleife (für die Anzeige). */
  taken = new Map<string, Field>();
  rounds = new Map<string, number>();
  private pos = 0;
  private readonly gen: Generator<string, void, void>;
  private readonly inputs: number[];
  private readonly maxSteps: number;

  constructor(prog: Program, inputs: number[], maxSteps: number) {
    this.inputs = inputs;
    this.maxSteps = maxSteps;
    this.gen = this.seq(prog.body);
    this.advance();
  }

  get ausgabe(): number[] { return this.werte.filter((v): v is number => typeof v === "number"); }
  get texte(): string[] { return this.werte.map(String); }

  /** Führt den aktuellen Block aus und läuft bis vor den nächsten. */
  step(): void {
    if (this.status !== "läuft" || this.cur === null) return;
    if (this.schritte >= this.maxSteps) {
      this.status = "endlosschleife";
      this.ref = this.cur;
      this.fehler = `Nach ${this.maxSteps} Schritten noch nicht fertig -- Endlosschleife?`;
      return;
    }
    this.schritte++;
    this.advance();
  }

  runToEnd(): this {
    while (this.status === "läuft") this.step();
    return this;
  }

  private advance(): void {
    try {
      const r = this.gen.next();
      if (r.done) { this.cur = null; this.status = "beendet"; } else this.cur = r.value;
    } catch (err) {
      if (err instanceof Endless) return;
      if (!(err instanceof RunError)) throw err;
      this.status = "laufzeitfehler";
      this.fehler = err.message;
      this.ref = this.cur;
    }
  }

  private cond(node: Code | null): boolean { return Boolean(evaluate(node as Node, this.env)); }
  private num(node: Node): number { return evaluate(node, this.env) as number; }

  private *seq(blocks: Block[] | undefined): Generator<string, void, void> {
    for (const b of blocks ?? []) yield* this.block(b);
  }

  // Jedes „yield b.id“ ist ein Takt: Der Block ist als Nächstes dran; der Code danach läuft
  // erst beim nächsten step().
  private *block(b: Block): Generator<string, void, void> {
    switch (b.type) {
      case "anweisung": {
        yield b.id;
        const [name, node] = b.code as [string, Node];
        this.env.set(name, this.num(node));
        return;
      }
      case "eingabe":
        yield b.id;
        for (const name of b.code as string[]) {
          if (this.pos >= this.inputs.length) {
            throw new RunError(`Keine Eingabe mehr für ${name} (es gibt nur ${this.inputs.length})`);
          }
          this.env.set(name, this.inputs[this.pos++]);
        }
        return;
      case "ausgabe":
        yield b.id;
        for (const item of b.code as Node[]) this.werte.push(item.k === "text" ? item.value : this.num(item));
        return;
      case "verzweigung": {
        yield b.id;
        const branch: Field = this.cond(b.code) ? "ja" : "nein";
        this.taken.set(b.id, branch);
        yield* this.seq(b.children[branch]);
        return;
      }
      case "solange":
        this.rounds.set(b.id, 0);
        for (;;) {
          yield b.id;
          if (!this.cond(b.code)) break;
          this.rounds.set(b.id, this.rounds.get(b.id)! + 1);
          yield* this.seq(b.children.body);
        }
        return;
      case "wiederhole":
        this.rounds.set(b.id, 0);
        for (;;) {
          this.rounds.set(b.id, this.rounds.get(b.id)! + 1);
          yield* this.seq(b.children.body);
          yield b.id;
          if (this.cond(b.code)) break;
        }
        return;
      case "zaehlschleife": {
        yield b.id;
        const [name, start, end, step] = b.code as Count;
        const first = this.num(start), last = this.num(end);
        const s = step === null ? 1 : this.num(step);
        if (s === 0) throw new RunError("Schrittweite 0 -- die Zählschleife käme nie ans Ende");
        this.env.set(name, first);
        this.rounds.set(b.id, 0);
        while (s > 0 ? this.env.get(name)! <= last : this.env.get(name)! >= last) {
          this.rounds.set(b.id, this.rounds.get(b.id)! + 1);
          yield* this.seq(b.children.body);
          yield b.id;
          this.env.set(name, checked(this.env.get(name)! + s));
        }
        return;
      }
    }
  }
}

// ------------------------------------------------------------------------- Prüfen

export type CaseStatus = "richtig" | "falsch" | "endlosschleife" | "laufzeitfehler";
export interface CaseResult {
  status: CaseStatus; ausgabe: Wert[]; texte: string[];
  fehler: string | null; ref: string | null; schritte: number;
}
export interface CheckResult {
  solved: boolean; problems: Problem[]; testfaelle: CaseResult[] | null;
  falsche_testfaelle: number[]; bloecke: number; par_erreicht: boolean;
  schleife: boolean; verschachtelt: boolean;
}

export const BLOCK_LABEL: Record<string, string> = {
  anweisung: "Anweisung", eingabe: "Eingabe", ausgabe: "Ausgabe", verzweigung: "Verzweigung",
  solange: "Solange-Schleife", wiederhole: "Wiederhole-bis-Schleife", zaehlschleife: "Zählschleife",
};

export function blockCounts(data: Struktogramm): Map<string, number> {
  const counts = new Map<string, number>();
  const walk = (raw: SBlock[] | undefined, depth: number) => {
    for (const b of raw ?? []) {
      const t = String(b.type ?? "").toLowerCase();
      if (!isBlockType(t)) continue;
      counts.set(t, (counts.get(t) ?? 0) + 1);
      if (depth < MAX_DEPTH) for (const key of childrenOf(t)) walk(b[key], depth + 1);
    }
  };
  walk(data.body, 0);
  return counts;
}

const hasText = (xs: Wert[]) => xs.some(v => typeof v === "string");
const norm = (v: Wert) => String(v).trim().toLowerCase();

/** Vergleich wie tasks.outputs_match: ohne Text-Sollwerte zählen nur Zahlen. */
export function outputsMatch(werte: Wert[], soll: Wert[]): boolean {
  if (!hasText(soll)) {
    const nums = werte.filter(v => typeof v === "number");
    return nums.length === soll.length && nums.every((v, i) => v === soll[i]);
  }
  return werte.length === soll.length && werte.every((v, i) => norm(v) === norm(soll[i]));
}

export function shownOutputs(werte: Wert[], soll: Wert[]): Wert[] {
  return hasText(soll) ? [...werte] : werte.filter(v => typeof v === "number");
}

export function check(task: Task, data: Struktogramm): CheckResult {
  const problems: Problem[] = [];
  for (const [type, n] of blockCounts(data)) {
    const limit = task.bloecke[type];
    if (limit !== undefined && n > limit) {
      problems.push({ code: "too_many_blocks", ref: type,
                      message: limit ? `Höchstens ${limit}× ${BLOCK_LABEL[type]} erlaubt`
                                     : `${BLOCK_LABEL[type]} ist in dieser Aufgabe nicht erlaubt` });
    }
  }
  const prog = compileProgram(data);
  problems.push(...prog.problems);
  const result: CheckResult = {
    solved: false, problems, testfaelle: null, falsche_testfaelle: [], bloecke: prog.blockCount,
    par_erreicht: false, schleife: prog.hasLoop, verschachtelt: prog.nested,
  };
  if (prog.problems.length) return result;

  result.testfaelle = task.testfaelle.map(tf => {
    const r = new Runner(prog, tf.eingabe, task.max_schritte).runToEnd();
    let status = r.status as CaseStatus;
    if (r.status === "beendet") status = outputsMatch(r.werte, tf.ausgabe) ? "richtig" : "falsch";
    return { status, ausgabe: shownOutputs(r.werte, tf.ausgabe), texte: r.texte, fehler: r.fehler,
             ref: r.ref, schritte: r.schritte };
  });
  result.falsche_testfaelle = result.testfaelle.flatMap((r, i) => (r.status === "richtig" ? [] : [i]));
  result.solved = !problems.length && !result.falsche_testfaelle.length;
  result.par_erreicht = result.solved && (task.par === null || prog.blockCount <= task.par);
  return result;
}

/** Sterne wie in GateLab: ★ gelöst, ★★ mit ≤ par Blöcken, ★★★ zusätzlich ohne Hinweis. */
export function starsFor(r: CheckResult, hintsUsed: number): number {
  if (!r.solved) return 0;
  return 1 + (r.par_erreicht ? 1 : 0) + (r.par_erreicht && hintsUsed === 0 ? 1 : 0);
}

// ------------------------------------------------------------------------- Pseudocode-Ansicht

/** Das Struktogramm als eingerückter Pseudocode (nur zum Lesen, F6). */
export function toPseudocode(data: Struktogramm): string {
  const lines: string[] = [];
  const t = (b: SBlock) => (b.text ?? "").trim() || "…";
  const strip = (s: string, re: RegExp) => s.replace(re, "");
  const walk = (blocks: SBlock[] | undefined, ind: string) => {
    if (!blocks?.length) { lines.push(`${ind}(nichts)`); return; }
    for (const b of blocks) {
      switch (b.type) {
        case "anweisung": lines.push(ind + t(b)); break;
        case "eingabe": lines.push(`${ind}EINGABE ${strip(t(b), INPUT_PREFIX)}`); break;
        case "ausgabe": lines.push(`${ind}AUSGABE ${strip(t(b), OUTPUT_PREFIX)}`); break;
        case "verzweigung":
          lines.push(`${ind}WENN ${strip(t(b), COND_PREFIX.wenn)} DANN`);
          walk(b.ja, ind + "    ");
          if (b.nein?.length) { lines.push(`${ind}SONST`); walk(b.nein, ind + "    "); }
          lines.push(`${ind}ENDE WENN`);
          break;
        case "solange":
          lines.push(`${ind}SOLANGE ${strip(t(b), COND_PREFIX.solange)} TUE`);
          walk(b.body, ind + "    ");
          lines.push(`${ind}ENDE SOLANGE`);
          break;
        case "wiederhole":
          lines.push(`${ind}WIEDERHOLE`);
          walk(b.body, ind + "    ");
          lines.push(`${ind}BIS ${strip(t(b), COND_PREFIX.bis)}`);
          break;
        case "zaehlschleife":
          lines.push(`${ind}FÜR ${strip(t(b), /^\s*(?:für|fuer|for)\s+/i)} TUE`);
          walk(b.body, ind + "    ");
          lines.push(`${ind}ENDE FÜR`);
          break;
      }
    }
  };
  walk(data.body, "");
  return lines.join("\n");
}
